import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Media } from '@askporter/utils';
import { GalleryItem } from './GalleryItem';
import { GalleryVideo } from './GalleryVideo';
import { PreviewImage } from './PreviewImage';

export interface SmartBoxGalleryProps {
  /**
   * Array of media files to display in the gallery
   */
  images: Media[];
  /**
   * Maximum width (and height) for each image in pixels. Images are always square.
   */
  maxWidth?: number;
}

/**
 * SmartBoxGallery displays images in an intelligent grid layout that adapts based on the number of images.
 * - 1 image: Max 300px wide, 222px high (natural sizing with constraints, no empty space)
 * - 2 images: Stacked vertically
 * - 3 images: Two on the left, one tall on the right
 * - 4 images: 2x2 grid
 * - 5+ images: Shows first 4 with a "+N" overlay. Clicking expands to show all images in a 2-column grid
 */
export const SmartBoxGallery: React.FC<SmartBoxGalleryProps> = ({ images, maxWidth = 300 }) => {
  const [selectedImage, setSelectedImage] = useState<Media | undefined>();
  const [isExpanded, setIsExpanded] = useState(false);

  const imageCount = images.length;
  const hasMoreThanFour = imageCount > 4;
  const shouldShowCompact = hasMoreThanFour && !isExpanded;
  const displayImages = shouldShowCompact ? images.slice(0, 4) : images;
  const remainingCount = imageCount - 4;

  // Special rendering for single image/video - no grid, just natural sizing with constraints
  if (imageCount === 1) {
    const file = images[0];
    const mediaSrc = file.path || file.tempPath;
    const isVideo = file.nameAndType?.type === 'mp4' || file.nameAndType?.type === 'hevc';
    const ariaLabel = `${file.nameAndType?.name || 'Media file'} - click to preview`;

    return (
      <>
        <Box
          role="button"
          tabIndex={0}
          aria-label={ariaLabel}
          sx={{
            maxWidth: '300px',
            maxHeight: '222px',
            borderRadius: '8px',
            overflow: 'hidden',
            cursor: 'pointer',
            position: 'relative',
            backgroundColor: isVideo ? '#000' : 'transparent',
          }}
          onClick={() => setSelectedImage(file)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setSelectedImage(file);
            }
          }}
        >
          {isVideo ? (
            <GalleryVideo
              src={mediaSrc || ''}
              alt={file.nameAndType?.name || 'Video'}
              width="auto"
              height="auto"
              maxWidth="300px"
              maxHeight="222px"
            />
          ) : (
            <img
              src={mediaSrc || ''}
              alt={file.nameAndType?.name || 'Gallery image'}
              style={{
                width: 'auto',
                height: 'auto',
                maxWidth: '300px',
                maxHeight: '222px',
                display: 'block',
                borderRadius: '8px',
              }}
            />
          )}
        </Box>

        <PreviewImage image={selectedImage} onClose={() => setSelectedImage(undefined)} />
      </>
    );
  }

  // Calculate dimensions based on image count and expanded state
  const getContainerDimensions = () => {
    if (imageCount === 2) {
      return { width: maxWidth, height: maxWidth * 2 + 8 }; // 2 images stacked + gap
    }
    if (imageCount === 3) {
      return { width: maxWidth * 2 + 8, height: maxWidth * 2 + 8 }; // 2x2 grid + gap
    }
    // 4 or more images
    if (shouldShowCompact) {
      return { width: maxWidth * 2 + 8, height: maxWidth * 2 + 8 }; // 2x2 grid + gap
    }
    // Expanded: 2-column grid
    const rows = Math.ceil(imageCount / 2);
    return { width: maxWidth * 2 + 8, height: maxWidth * rows + 8 * (rows - 1) };
  };

  const { width: containerWidth, height: containerHeight } = getContainerDimensions();

  // Determine grid layout
  const getGridLayout = () => {
    if (imageCount === 2) {
      return { columns: '1fr', rows: '1fr 1fr' };
    }
    if (imageCount === 3 && !isExpanded) {
      return { columns: '1fr 1fr', rows: '1fr 1fr' };
    }
    // Default: 2-column grid
    return { columns: '1fr 1fr', rows: 'auto' };
  };

  const { columns, rows } = getGridLayout();

  return (
    <>
      <Box
        role="list"
        aria-label={`Gallery with ${imageCount} ${imageCount === 1 ? 'item' : 'items'}`}
        sx={{
          width: containerWidth,
          height: containerHeight,
          display: 'grid',
          gap: '8px',
          gridTemplateColumns: columns,
          gridTemplateRows: rows,
        }}
      >
        {displayImages.map((file, idx) => {
          const isLastImageInCompactView = shouldShowCompact && idx === 3;
          const isThirdImageInThreeLayout = imageCount === 3 && idx === 2 && !isExpanded;
          const itemHeight = isThirdImageInThreeLayout ? maxWidth * 2 + 8 : maxWidth;
          const fileId = file.file.fileUrn || file.file.filePath || idx;

          return (
            <Box
              key={fileId}
              role="listitem"
              sx={{
                position: 'relative',
                gridColumn: isThirdImageInThreeLayout ? '2 / 3' : 'auto',
                gridRow: isThirdImageInThreeLayout ? '1 / 3' : 'auto',
              }}
            >
              <GalleryItem
                file={file}
                width={maxWidth}
                height={itemHeight}
                canPreview={!isLastImageInCompactView}
                onPreview={() => setSelectedImage(file)}
              />
              {isLastImageInCompactView && (
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label={`Show ${remainingCount} more ${remainingCount === 1 ? 'item' : 'items'}`}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    borderRadius: '8px',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsExpanded(true);
                    }
                  }}
                >
                  <Typography
                    variant="h3"
                    aria-hidden="true"
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '48px',
                    }}
                  >
                    +{remainingCount}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <PreviewImage image={selectedImage} onClose={() => setSelectedImage(undefined)} />
    </>
  );
};

export { PreviewImage, type PreviewImageProps } from './PreviewImage';
